import { useCallback, useEffect, useRef } from "react";

export interface UseWaitForOptions {
  pollInterval?: number;
  timeout?: number;
}

/**
 * A React hook that returns an async function to wait for a specific state condition to be met.
 *
 * @template T The type of the state being watched.
 * @param state The state value to monitor.
 * @param conditionFn A function that takes the current state and returns true when the desired condition is met.
 * @param options Optional configuration for polling interval and timeout.
 * @returns An asynchronous function that returns a Promise. The Promise resolves when the condition is met, or rejects on timeout or if the condition function throws.
 */
const useWaitFor = <T>(
  state: T,
  conditionFn: (currentState: T) => boolean,
  options: UseWaitForOptions = {},
): (() => Promise<void>) => {
  const { pollInterval = 50, timeout = 15000 } = options;

  const stateRef = useRef<T>(state);
  const conditionFnRef = useRef<(currentState: T) => boolean>(conditionFn);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    conditionFnRef.current = conditionFn;
  }, [conditionFn]);

  const waitForCondition = useCallback(async (): Promise<void> => {
    try {
      if (conditionFnRef.current(stateRef.current)) {
        return Promise.resolve();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return Promise.reject(new Error(`useWaitFor: Initial condition check failed: ${message}`));
    }

    return new Promise<void>((resolve, reject) => {
      let intervalId: ReturnType<typeof setInterval> | null = null;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const cleanup = () => {
        if (intervalId) clearInterval(intervalId);
        if (timeoutId) clearTimeout(timeoutId);
      };

      const checkCondition = () => {
        try {
          if (conditionFnRef.current(stateRef.current)) {
            cleanup();
            resolve();
          }
        } catch (error) {
          cleanup();
          const message = error instanceof Error ? error.message : String(error);
          reject(new Error(`useWaitFor: ${message}`));
        }
      };

      intervalId = setInterval(checkCondition, pollInterval);

      if (timeout) {
        timeoutId = setTimeout(() => {
          cleanup();
          reject(new Error(`useWaitFor: Condition not met within ${timeout}ms.`));
        }, timeout);
      }
    });
  }, [pollInterval, timeout]);

  return waitForCondition;
};

export default useWaitFor;
